import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * 🤖 ChatAIWidget - Widget flutuante de chat IA 24/7
 * - Persistente via sessionStorage (mesma aba mantém conversa)
 * - Funciona para anônimos e logados
 * - Bottom-right floating bubble, expande ao clicar
 * - Mobile-first responsive
 */
export const ChatAIWidget = () => {
  const location = useLocation();
  // Não mostra o widget em páginas que já são de chat fullscreen
  const isChatFullscreenPage = ['/chat', '/ajuda', '/duvidas'].includes(location.pathname);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content:
        'Olá! 👋 Sou a atendente virtual da Minha Música Registrada. Posso tirar suas dúvidas sobre:\n\n🎵 Como registrar suas músicas\n📜 Lei 9.610/98 e ECAD\n🤖 Uso de IA na composição\n💰 Planos e preços\n\nComo posso te ajudar?',
    },
  ]);
  const [hasUnread, setHasUnread] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Session ID persistente por aba
  const getSessionId = () => {
    let sid = sessionStorage.getItem('mmr-chat-sid');
    if (!sid) {
      sid = 'chat-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
      sessionStorage.setItem('mmr-chat-sid', sid);
    }
    return sid;
  };

  const sessionId = getSessionId();

  // Scroll automático
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus no input ao abrir
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setHasUnread(false);
    }
  }, [open]);

  const sendMessage = useCallback(async () => {
    const text = (input || '').trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(
        `${BACKEND_URL}/api/chat/ask`,
        { session_id: sessionId, message: text },
        { headers, timeout: 30000 }
      );
      const reply = res.data?.reply || 'Desculpe, tive um problema. Tente de novo?';
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
      if (!open) setHasUnread(true);
    } catch (e) {
      const errMsg =
        e.response?.status === 400
          ? 'Mensagem inválida. Tente reformular?'
          : 'Desculpe, tive uma instabilidade. Tente novamente em alguns segundos 💛';
      setMessages((prev) => [...prev, { role: 'assistant', content: errMsg }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, sessionId, open]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Perguntas sugeridas
  const suggestions = [
    '🎵 Como registrar minha música?',
    '💰 Quais são os planos?',
    '🤖 Posso usar IA na composição?',
    '🌍 Tem validade internacional?',
  ];

  // Se estamos em página de chat fullscreen, não renderiza o widget flutuante
  if (isChatFullscreenPage) return null;

  return (
    <>
      {/* Bolha flutuante */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-widget-open-btn"
          className="fixed bottom-24 right-5 sm:bottom-28 sm:right-6 z-[60] w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-200 flex items-center justify-center group"
          aria-label="Tire suas dúvidas aqui 24H"
        >
          <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          {hasUnread && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>
          )}
          {/* Tooltip permanente — aparece logo após carregar */}
          <span className="absolute right-16 sm:right-20 top-1/2 -translate-y-1/2 bg-blue-600 text-white text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap shadow-lg hidden sm:flex items-center gap-1 animate-pulse-slow">
            <span className="text-base">💬</span> Tire suas dúvidas aqui! 24H
            <span className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 bg-blue-600 rotate-45"></span>
          </span>
        </button>
      )}

      {/* Painel expandido */}
      {open && (
        <div
          className="fixed bottom-0 right-0 sm:bottom-4 sm:right-4 z-[70] w-full sm:w-96 h-[100dvh] sm:h-[600px] sm:max-h-[85vh] bg-white sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
          data-testid="chat-widget-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-xl">
                🎵
              </div>
              <div>
                <h3 className="font-bold text-sm">Tire suas dúvidas aqui! 24H</h3>
                <p className="text-xs text-blue-100 flex items-center gap-1">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                  Online agora
                </p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chat-widget-close-btn"
              className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Fechar chat"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Mensagens */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50"
            data-testid="chat-messages-container"
          >
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-sm'
                      : 'bg-white text-gray-800 shadow-sm border border-gray-200 rounded-bl-sm'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white shadow-sm border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1.5">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></span>
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></span>
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></span>
                </div>
              </div>
            )}
          </div>

          {/* Sugestões (só aparecem no início) */}
          {messages.length <= 1 && !loading && (
            <div className="px-4 pb-2 bg-gray-50 flex flex-wrap gap-2">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setInput(s.replace(/^[^\s]+\s/, ''))}
                  className="text-xs px-3 py-1.5 rounded-full bg-white hover:bg-blue-50 border border-gray-200 text-gray-700 transition-colors"
                  data-testid={`chat-suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-3 bg-white border-t border-gray-200 flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={loading}
              placeholder="Digite sua dúvida..."
              data-testid="chat-input"
              className="flex-1 px-4 py-2 rounded-full border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm disabled:bg-gray-100 outline-none"
              maxLength={500}
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              data-testid="chat-send-btn"
              className="w-10 h-10 rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white flex items-center justify-center transition-colors flex-shrink-0"
              aria-label="Enviar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>

          {/* Rodapé */}
          <div className="px-3 py-2 bg-gray-50 border-t border-gray-100 text-[10px] text-gray-500 text-center">
            Respostas automáticas por IA • Para casos complexos: sacminhascomposicoes@gmail.com
          </div>
        </div>
      )}
    </>
  );
};

export default ChatAIWidget;
